// webapp/src/lib/offlineWriteQueue.js
//
// Phase 2 / 2c offline write queue. Stores POST bodies for sales, visitors,
// animals and journals in IndexedDB when the network is unavailable, then
// replays them in dependency order on `online` events. tempIds (negative
// numbers) flow through the UI while the entries are queued, and are
// rewritten to real ids during drain so cross-record references survive.
//
// Entry shape (all fields kept on disk):
//   { tempId, type, url, saleData, calledFrom, createdAt, attempts,
//     lastAttemptAt, lastError, status }
//
//   type    — 'sale' | 'visitor' | 'animal' | 'journal' | 'article'
//   url     — POST target (e.g. '/api/visitors/')
//   saleData — body to POST. Field name kept for storage backwards-compat.
//
// Public API:
//   enqueueInsert({type, url, body, calledFrom}) -> tempId | null   // generic
//   enqueueSale(saleData, calledFrom) -> tempId | null              // sugar
//   mutateInsert(tempId, mutator) -> entry | null
//   drain({ onReplaced, onFailed }) -> { ok, transient, permanent, skipped }
//   getAll() -> Array<entry>
//   markRetry(tempId) -> void
//   discard(tempId) -> void

import util from 'preact-util';
import { tx } from './offlineStorage';
import { isNetworkErrorEnvelope } from './offlineCache';

const STORE = 'pendingSales';

const BACKOFF_MS = [10_000, 30_000, 120_000, 600_000];

export function backoffMs(attempts) {
    const i = Math.min(Math.max(attempts - 1, 0), BACKOFF_MS.length - 1);
    return BACKOFF_MS[i];
}

/**
 * Real IDB-backed storage for the pendingSales store. The store uses
 * keyPath 'tempId' so put/delete take the entry / key directly.
 */
function makeIdbStorage() {
    return {
        getAll: () => tx('readonly', STORE, store => store.getAll()),
        put: (entry) => tx('readwrite', STORE, store => store.put(entry)),
        delete: (tempId) => tx('readwrite', STORE, store => store.delete(tempId)),
        get: (tempId) => tx('readonly', STORE, store => store.get(tempId)),
    };
}

let _defaultStorage = null;
function defaultStorage() {
    if (!_defaultStorage) _defaultStorage = makeIdbStorage();
    return _defaultStorage;
}

/**
 * Test-only: build an in-memory storage with the same shape as makeIdbStorage.
 * Used by unit tests; production callers pass nothing.
 */
export function _makeMemoryStorage() {
    const m = new Map();
    return {
        getAll: async () => Array.from(m.values()),
        put: async (entry) => { m.set(entry.tempId, entry); },
        delete: async (tempId) => { m.delete(tempId); },
        get: async (tempId) => (m.has(tempId) ? m.get(tempId) : null),
        _map: m,
    };
}

let _tempIdCounter = 0;
function nextTempId() {
    _tempIdCounter += 1;
    return -(Date.now() + _tempIdCounter);
}

// Convert MobX observables (and any other non-cloneable graph members) to a
// plain JSON-safe object before handing it to IndexedDB. structured-clone
// will reject Proxies / functions / circular refs, but JSON.parse(stringify)
// produces something IDB can swallow and is what the eventual POST will send
// over the wire anyway.
function toPlain(value) {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (e) {
        return null;
    }
}

/**
 * Generic queue insert. Used by every store that wants to queue an offline
 * POST: sales, visitors, animals, journals (and anything else we add later).
 *
 *   type:       'sale' | 'visitor' | 'animal' | 'journal'
 *   url:        POST target (drain uses this, defaulting to /api/sales/ for
 *               legacy entries that have no `url`).
 *   body:       request body. Round-tripped through JSON to drop MobX proxies.
 *   calledFrom: optional caller tag for debugging.
 *
 * Returns the assigned tempId (a unique negative number) on success, or null
 * if IDB is unavailable / the body isn't JSON-serializable.
 */
export async function enqueueInsert(opts, storage) {
    const { type = 'sale', url = '/api/sales/', body, calledFrom = null } = opts || {};
    const plainBody = toPlain(body);
    if (!plainBody) {
        console.warn('[offlineWriteQueue] enqueueInsert: body not JSON-serializable, dropping', { type });
        return null;
    }
    const store = storage || defaultStorage();
    try {
        const tempId = nextTempId();
        const entry = {
            tempId,
            type,
            url,
            saleData: plainBody, // legacy field name kept for storage compat
            calledFrom,
            createdAt: Date.now(),
            attempts: 0,
            lastAttemptAt: null,
            lastError: null,
            status: 'pending',
        };
        await store.put(entry);
        console.log(`[offlineWriteQueue] enqueued ${type}`, { tempId, url });
        return tempId;
    } catch (e) {
        console.warn('[offlineWriteQueue] enqueueInsert storage.put failed', { type, err: e });
        return null;
    }
}

// Sugar — kept so existing callers (saleStore.saveSale) don't have to change.
export async function enqueueSale(saleData, calledFrom, storage = defaultStorage()) {
    return enqueueInsert(
        { type: 'sale', url: '/api/sales/', body: saleData, calledFrom },
        storage,
    );
}

export async function getAll(storage = defaultStorage()) {
    try {
        const all = await storage.getAll();
        return Array.isArray(all) ? all : [];
    } catch (e) {
        return [];
    }
}

export async function discard(tempId, storage = defaultStorage()) {
    try {
        await storage.delete(tempId);
    } catch (e) {
        // best-effort
    }
}

/**
 * Refresh the saleData on a queued insert. Used when a tempId sale is edited
 * offline — every keystroke / addProduct / addAnimal calls saveSale again,
 * and we want the eventual drained POST to send the latest body, not the
 * one captured at original enqueue time.
 *
 * `mutator(prevSaleData)` returns the next saleData. Returns the updated
 * entry on success, or null if the tempId isn't queued (caller decides what
 * to do — typically fall back to a fresh enqueue).
 */
export async function mutateInsert(tempId, mutator, storage = defaultStorage()) {
    try {
        const entry = await storage.get(tempId);
        if (!entry) {
            console.warn('[offlineWriteQueue] mutateInsert: no entry for tempId', tempId);
            return null;
        }
        // Only insert entries are mutable in place. Phase 2c will introduce
        // op:'update' entries; those are handled by their own coalescer.
        if (entry.op && entry.op !== 'insert') return null;
        const mutated = mutator(entry.saleData || {});
        const nextSaleData = toPlain(mutated);
        if (!nextSaleData) {
            console.warn('[offlineWriteQueue] mutateInsert: refreshed body not JSON-serializable');
            return null;
        }
        const next = { ...entry, saleData: nextSaleData };
        await storage.put(next);
        console.log('[offlineWriteQueue] mutated insert', { tempId, products: (nextSaleData.products || []).length });
        return next;
    } catch (e) {
        console.warn('[offlineWriteQueue] mutateInsert failed', e);
        return null;
    }
}

export async function markRetry(tempId, storage = defaultStorage()) {
    try {
        const entry = await storage.get(tempId);
        if (!entry) return;
        entry.status = 'pending';
        entry.attempts = 0;
        entry.lastError = null;
        entry.lastAttemptAt = null;
        await storage.put(entry);
    } catch (e) {
        // best-effort
    }
}

/**
 * Default POST function used in production. Tests inject `postFn` instead.
 * Sends `body` to the entry's `url` (legacy entries default to /api/sales/).
 */
async function defaultPostFn(body, url) {
    return util.fetchApi(url || '/api/sales/', { method: 'POST' }, body);
}

// Drain processing order — must respect FK direction so tempIds resolve in
// time for dependent entries. visitor → animal → journal → sale.
const DRAIN_ORDER = ['visitor', 'animal', 'journal', 'sale', 'article'];

// For a successful POST response, pull out the new server-side id. Each
// resource serializes its created record under a different key.
function extractRealId(response, type) {
    if (!response || !response.data) return null;
    const node = response.data[type];
    if (node && node.id != null) return node.id;
    // Sale responses also use response.data.sale.id; visitor uses
    // response.data.visitor.id; same shape across the API.
    return null;
}

// Rewrite cross-record tempId references in `body` against the resolution
// map built up during drain. Handles the foreign keys we know about; any
// future ref needs to be added here. Returns a new object — never mutates.
function rewriteRefs(body, type, map) {
    if (!body || typeof body !== 'object' || map.size === 0) return body;
    const out = { ...body };
    if (type === 'animal') {
        if (out.owner != null && map.has(out.owner)) out.owner = map.get(out.owner);
    } else if (type === 'journal') {
        if (out.animal != null && map.has(out.animal)) out.animal = map.get(out.animal);
        if (out.sale != null && map.has(out.sale)) out.sale = map.get(out.sale);
        if (out.visitor != null && map.has(out.visitor)) out.visitor = map.get(out.visitor);
    } else if (type === 'sale') {
        // The embedded animal snapshot carries its own `owner`, so mapping only
        // `.id` left the sale holding a customer reference the server could not
        // resolve even once the animal row itself was correct.
        const itemRefs = { visitors: ['id'], animals: ['id', 'owner'], journals: ['id'] };
        for (const arr of Object.keys(itemRefs)) {
            if (Array.isArray(out[arr])) {
                out[arr] = out[arr].map(item => {
                    if (!item) return item;
                    let next = item;
                    itemRefs[arr].forEach(field => {
                        if (next[field] != null && map.has(next[field])) {
                            next = { ...next, [field]: map.get(next[field]) };
                        }
                    });
                    return next;
                });
            }
        }
    }
    return out;
}

/**
 * Persist a resolved tempId -> realId into every entry that is STILL queued.
 *
 * `tempToReal` below only lives for the duration of one drain pass, so an entry
 * that posts in a later pass used to be rewritten against an empty map and went
 * to the server carrying the negative tempId — permanently. (Seen in production:
 * a customer created offline synced to a real id while the sale, animal and
 * journal that referenced it kept `-1787912423491`, leaving the sale with no
 * resolvable owner and no way to transfer it to Fiken.)
 *
 * Writing the rewrite straight into the queued bodies means the mapping does not
 * have to survive the pass: whatever is still on disk is already correct.
 *
 * `settled` holds the tempIds whose entries have just been deleted after a
 * successful POST — re-putting one of those would resurrect it and double-post
 * the record on the next drain.
 */
async function persistRefRewrite(storage, groups, settled, tempId, realId) {
    const map = new Map([[tempId, realId]]);
    for (const type of DRAIN_ORDER) {
        for (const entry of groups[type] || []) {
            if (!entry || settled.has(entry.tempId)) continue;
            const before = JSON.stringify(entry.saleData);
            const rewritten = rewriteRefs(entry.saleData, type, map);
            if (JSON.stringify(rewritten) === before) continue;
            entry.saleData = rewritten;
            try {
                // eslint-disable-next-line no-await-in-loop
                await storage.put(entry);
            } catch (e) {
                // Best effort: a failed put leaves the in-memory rewrite in place,
                // which still fixes this pass.
            }
        }
    }
}

function isTransientFailure(response) {
    if (!response) return true;
    if (isNetworkErrorEnvelope(response)) return true;
    const s = response.status;
    if (typeof s !== 'number') return true;
    if (s === 429) return true;
    if (s >= 500) return true;
    return false;
}

function isPermanentFailure(response) {
    if (!response) return false;
    const s = response.status;
    return typeof s === 'number' && s >= 400 && s < 500 && s !== 429;
}

/**
 * Replay every drainable pending entry, processing in dependency order so
 * tempIds resolve before their dependents POST. Each entry's processing
 * decision:
 *   - skip if status === 'failed'
 *   - skip if attempts > 0 and (now - lastAttemptAt) < backoffMs(attempts)
 *   - else POST; on success delete + onReplaced(type, tempId, realId, response)
 *   - else if transient: bump attempts + lastAttemptAt, leave status='pending'
 *   - else permanent: set status='failed' + lastError, onFailed(entry)
 *
 * Cross-record tempId references inside `body` (animal.owner, journal.sale,
 * sale.visitors[].id, …) are rewritten against the in-memory resolution map
 * built up as inserts succeed.
 *
 * Returns counters: { ok, transient, permanent, skipped }.
 */
// Drains are triggered from several places (app boot, 'online' events,
// manual retry in the pending-sales route). Two drains running at once
// would read the same queue snapshot and double-POST every entry —
// duplicate visitors/sales server-side — so a drain that starts while
// another is in flight shares that run instead.
let _drainInFlight = null;

export async function drain(opts = {}) {
    if (_drainInFlight) return _drainInFlight;
    _drainInFlight = drainImpl(opts);
    try {
        return await _drainInFlight;
    } finally {
        _drainInFlight = null;
    }
}

// Articles queued with offline-stashed images carry pending descriptors
// (`_pendingUpload: true, pendingImageKey`). Before the article POST, upload
// each stashed blob and splice the real S3 descriptor into the body.
// Progress persists via storage.put after every upload so an interrupted
// drain re-uploads only what's left. A missing blob (already uploaded, or
// evicted) drops that image — a post with fewer images beats a stuck queue.
// Returns { ok: true } | { ok: false, permanent, error? }.
async function prepareArticleImages(entry, storage, uploadPendingImageFn) {
    const source = Array.isArray(entry.saleData && entry.saleData.images) ? entry.saleData.images : [];
    if (!source.some(img => img && img._pendingUpload)) return { ok: true };
    // Lazy require — keeps this module light for the many callers that never drain articles.
    // eslint-disable-next-line global-require
    const uploadOne = uploadPendingImageFn || (key => require('./offlineImageQueue').uploadPendingImage(key));
    const images = source.slice();
    for (let i = 0; i < images.length; i += 1) {
        const img = images[i];
        if (!img || !img._pendingUpload) continue;
        try {
            // eslint-disable-next-line no-await-in-loop
            images[i] = await uploadOne(img.pendingImageKey);
        } catch (err) {
            if (err && err.isMissing) {
                images[i] = null;
            } else if (err && typeof err.status === 'number' && err.status >= 400 && err.status < 500 && err.status !== 429) {
                return { ok: false, permanent: true, error: { status: err.status, message: err.message || '', field: 'images' } };
            } else {
                return { ok: false, permanent: false };
            }
        }
        entry.saleData = { ...entry.saleData, images: images.filter(Boolean) };
        // eslint-disable-next-line no-await-in-loop
        try { await storage.put(entry); } catch (e) { /* ignore */ }
    }
    return { ok: true };
}

async function drainImpl(opts = {}) {
    const storage = opts.storage || defaultStorage();
    const postFn = opts.postFn || defaultPostFn;
    const onReplaced = opts.onReplaced || (() => {});
    const onFailed = opts.onFailed || (() => {});
    const uploadPendingImageFn = opts.uploadPendingImageFn || null;

    const counters = { ok: 0, transient: 0, permanent: 0, skipped: 0 };

    let entries;
    try {
        entries = await storage.getAll();
    } catch (e) {
        return counters;
    }
    if (!Array.isArray(entries) || entries.length === 0) return counters;

    // Group by type, preserving createdAt order within each group. Legacy
    // entries (no `type`) default to 'sale' so the Phase-2 queue keeps
    // working unchanged.
    const groups = { visitor: [], animal: [], journal: [], sale: [], article: [], _unknown: [] };
    for (const entry of entries) {
        if (!entry) continue;
        const t = entry.type || 'sale';
        (groups[t] || groups._unknown).push(entry);
    }
    for (const k of Object.keys(groups)) {
        groups[k].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    }

    // tempId -> realId map built up as inserts succeed; used to rewrite refs
    // in dependent entries. It only covers THIS pass — persistRefRewrite writes
    // every resolution into the queued bodies so later passes are covered too.
    const tempToReal = new Map();
    // tempIds whose entry has been deleted after a successful POST. Never write
    // one of these back to storage.
    const settled = new Set();

    const now = Date.now();
    const processOne = async (entry, type) => {
        if (entry.status === 'failed') {
            counters.skipped += 1;
            return;
        }
        if (entry.attempts > 0 && entry.lastAttemptAt
            && now - entry.lastAttemptAt < backoffMs(entry.attempts)) {
            counters.skipped += 1;
            return;
        }

        if (type === 'article') {
            const prep = await prepareArticleImages(entry, storage, uploadPendingImageFn);
            if (!prep.ok) {
                entry.attempts += 1;
                entry.lastAttemptAt = Date.now();
                if (prep.permanent) {
                    entry.status = 'failed';
                    entry.lastError = prep.error || { status: 0, message: 'image upload failed', field: 'images' };
                }
                try { await storage.put(entry); } catch (e) { /* ignore */ }
                if (prep.permanent) {
                    try { onFailed(type, entry); } catch (e) { /* ignore */ }
                    counters.permanent += 1;
                } else {
                    counters.transient += 1;
                }
                return;
            }
        }

        const body = rewriteRefs(entry.saleData, type, tempToReal);
        const url = entry.url || '/api/sales/';

        let response;
        try {
            response = await postFn(body, url);
        } catch (e) {
            response = null;
        }

        if (response && response.status >= 200 && response.status < 300) {
            const realId = extractRealId(response, type);
            if (realId != null) tempToReal.set(entry.tempId, realId);
            try { await storage.delete(entry.tempId); } catch (e) { /* ignore */ }
            settled.add(entry.tempId);
            if (realId != null) {
                await persistRefRewrite(storage, groups, settled, entry.tempId, realId);
            }
            try { onReplaced(type, entry.tempId, realId, response); } catch (e) { /* ignore */ }
            counters.ok += 1;
            return;
        }

        if (isPermanentFailure(response)) {
            entry.status = 'failed';
            entry.attempts += 1;
            entry.lastAttemptAt = Date.now();
            entry.lastError = {
                status: response.status,
                message: response.message || '',
                field: response.field || '',
            };
            try { await storage.put(entry); } catch (e) { /* ignore */ }
            try { onFailed(type, entry); } catch (e) { /* ignore */ }
            counters.permanent += 1;
            return;
        }

        if (isTransientFailure(response)) {
            entry.attempts += 1;
            entry.lastAttemptAt = Date.now();
            try { await storage.put(entry); } catch (e) { /* ignore */ }
            counters.transient += 1;
        }
    };

    for (const type of DRAIN_ORDER) {
        for (const entry of groups[type]) {
            // eslint-disable-next-line no-await-in-loop
            await processOne(entry, type);
        }
    }
    // Unrecognized types are NEVER replayed as another type (a mis-routed
    // POST is worse than a stalled entry). Surface them so a future version
    // that knows the type can drain them.
    for (const entry of groups._unknown) {
        counters.skipped += 1;
        try { onFailed(entry.type || 'unknown', entry); } catch (e) { /* ignore */ }
    }

    return counters;
}
